import React, { FC, useEffect, useRef } from 'react';
import { Animated, StyleSheet, TouchableOpacity, View } from 'react-native';

import { Typography } from './typography';

import colors from '@packages/core/styles/colors';

/** The sizes a toggle comes in: the default one, and the small one that sits in a line of small text. */
export type ToggleSize = 'default' | 'small';

interface ToggleProps {
    /** What a screen reader calls the switch, such as the label shown next to it. */
    accessibilityLabel?: string;
    disabled?: boolean;
    uncheckedLabel?: string;
    checkedLabel?: string;
    checked: boolean;
    onChange(checked: boolean): void;
    size?: ToggleSize;
}

export const Toggle: FC<ToggleProps> = ({
    accessibilityLabel,
    uncheckedLabel,
    checkedLabel,
    checked,
    onChange,
    disabled,
    size = 'default',
}) => {
    const {
        checkedLeft,
        disabledStyle,
        handleColors,
        handleStyle,
        slideColors,
        slideStyle,
        toggleStyle,
        uncheckedLeft,
    } = TOGGLE_LOOKS[size];
    const animationDuration = 200;
    const toggleAnimation = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        //@ts-ignore
        Animated.timing(toggleAnimation, {
            toValue: checked ? checkedLeft : uncheckedLeft,
            duration: animationDuration,
        }).start();
    }, [checked, checkedLeft, toggleAnimation, uncheckedLeft]);

    function handleTogglePress() {
        onChange(!checked);
    }

    return (
        <View style={[styles.toggleOuter, disabled && disabledStyle]}>
            {uncheckedLabel && (
                <Typography variant="label" style={styles.uncheckedLabel}>
                    {uncheckedLabel}
                </Typography>
            )}
            <TouchableOpacity
                accessibilityChecked={checked}
                accessibilityLabel={accessibilityLabel}
                accessibilityRole="switch"
                accessibilityState={{ checked }}
                style={toggleStyle}
                onPress={handleTogglePress}
                disabled={disabled}
            >
                <Animated.View
                    style={[
                        slideStyle,
                        {
                            backgroundColor: toggleAnimation.interpolate({
                                inputRange: [uncheckedLeft, checkedLeft],
                                outputRange: slideColors,
                            }),
                        },
                    ]}
                >
                    <Animated.View
                        style={[
                            handleStyle,
                            {
                                left: toggleAnimation,
                                backgroundColor: toggleAnimation.interpolate({
                                    inputRange: [uncheckedLeft, checkedLeft],
                                    outputRange: handleColors,
                                }),
                            },
                        ]}
                    />
                </Animated.View>
            </TouchableOpacity>
            {checkedLabel && (
                <Typography variant="label" style={styles.checkedLabel}>
                    {checkedLabel}
                </Typography>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    toggleOuter: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    uncheckedLabel: {
        marginRight: 16,
    },
    checkedLabel: {
        marginLeft: 16,
    },
    toggle: {
        width: 42,
        height: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    slide: {
        width: 34,
        height: 14,
        borderRadius: 20,
        position: 'relative',
        backgroundColor: colors.grayThree,
    },
    handle: {
        top: -3,
        left: 0,
        zIndex: 1,
        width: 20,
        height: 20,
        borderRadius: 10,
        position: 'absolute',
        shadowColor: colors.black,
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowOpacity: 0.21,
        shadowRadius: 3,
        elevation: 3,
    },
    disabled: {
        opacity: 0.6,
    },
    toggleSmall: {
        width: 24,
        height: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    slideSmall: {
        width: 24,
        height: 10,
        borderRadius: 7,
        position: 'relative',
    },
    handleSmall: {
        top: -2,
        left: 0,
        zIndex: 1,
        width: 14,
        height: 14,
        borderRadius: 7,
        position: 'absolute',
    },
});

/**
 * How the toggle is drawn at each size: its touch area, its slide and its handle, where the handle rests
 * off and on, and the colors of the slide and of the handle from off to on. A disabled small toggle keeps
 * its colors, where the default one fades.
 */
const TOGGLE_LOOKS = {
    default: {
        checkedLeft: 18,
        disabledStyle: styles.disabled,
        handleColors: [colors.graySix, colors.blueOne],
        handleStyle: styles.handle,
        slideColors: [colors.grayThree, colors.blueThree],
        slideStyle: styles.slide,
        toggleStyle: styles.toggle,
        uncheckedLeft: -4,
    },
    small: {
        checkedLeft: 10,
        disabledStyle: undefined,
        handleColors: [colors.brandDisabled, colors.blueOne],
        handleStyle: styles.handleSmall,
        slideColors: [colors.blueFour, colors.blueFour],
        slideStyle: styles.slideSmall,
        toggleStyle: styles.toggleSmall,
        uncheckedLeft: 0,
    },
} satisfies Record<ToggleSize, object>;
